import { MinusIcon } from '@linktr.ee/arbor/icons';
import { OTPInput, OTPInputContext, type OTPInputProps } from 'input-otp';
import {
  type ClipboardEvent,
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  forwardRef,
  useContext,
} from 'react';
import { cn } from '../utils/twUtils';

type InputOTPRootProps = Omit<
  OTPInputProps,
  | 'textAlign'
  | 'pushPasswordManagerStrategy'
  | 'pasteTransformer'
  | 'noScriptCSSFallback'
  | 'placeholder'
  | 'containerClassName'
  | 'render'
> & {
  onComplete?: OTPInputProps['onComplete'];
  inputMode?:
    'numeric' | 'text' | 'decimal' | 'tel' | 'search' | 'email' | 'url';
  className?: string;
  separator?: boolean;
};

export type InputOTPProps = InputOTPRootProps &
  (
    | { value: number | string; onChange: (newValue: number | string) => void }
    | { value?: never; onChange?: never }
  ) &
  (
    | { hasError: true; errorMessage: string | undefined }
    | { hasError?: false; errorMessage?: never }
  );

type InputOTPContextValue = {
  hasError?: boolean;
};

const InputOTPErrorContext = createContext<InputOTPContextValue>({});

export const Root = forwardRef<ElementRef<typeof OTPInput>, InputOTPProps>(
  (
    {
      className,
      children,
      errorMessage,
      hasError,
      inputMode = 'numeric',
      onComplete,
      onChange,
      value,
      autoFocus = false,
      maxLength,
      ...props
    },
    ref
  ) => {
    const handlePaste = (e: ClipboardEvent<HTMLDivElement>): void => {
      const pasteData = e.clipboardData.getData('text/plain').trim();

      if (pasteData.length === maxLength && typeof onChange === 'function') {
        e.preventDefault();
        onChange('');

        setTimeout(() => {
          if (typeof onChange === 'function') {
            onChange(pasteData);
          }
        }, 0);
      }
    };

    return (
      <InputOTPErrorContext.Provider value={{ hasError }}>
        <div className="flex flex-col gap-y-1">
          {/* Verification codes are read left-to-right regardless of UI direction. */}
          <div dir="ltr">
            <OTPInput
              ref={ref}
              value={value}
              onChange={onChange}
              onComplete={onComplete}
              containerClassName={cn(
                'flex items-center justify-center gap-2 has-[:disabled]:opacity-50 disabled:cursor-not-allowed',
                className
              )}
              maxLength={maxLength}
              autoFocus={autoFocus}
              inputMode={inputMode}
              textAlign="center"
              onPaste={handlePaste}
              {...props}
              data-testid="input-otp"
              pushPasswordManagerStrategy="none"
              data-1p-ignore="true"
              data-lpignore="true"
              data-protonpass-ignore="true"
              data-dashlane-disabled-on-field="true"
            >
              {children}
            </OTPInput>
          </div>
          {hasError && errorMessage && (
            <p
              className="mb-2 text-center text-caption-large-regular text-danger"
              aria-live="assertive"
            >
              {errorMessage}
            </p>
          )}
        </div>
      </InputOTPErrorContext.Provider>
    );
  }
);
Root.displayName = 'InputOTP';

export const Group = forwardRef<
  ElementRef<'div'>,
  ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  <div
    ref={ref}
    className="flex items-center justify-between gap-x-2 min-[390px]:gap-x-3 md:justify-center md:gap-x-4"
    {...props}
    data-testid="input-otp-group"
  />
));
Group.displayName = 'InputOTP.Group';

export const Slot = forwardRef<
  ElementRef<'div'>,
  ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, ...props }, ref) => {
  const { slots } = useContext(OTPInputContext);
  const { hasError } = useContext(InputOTPErrorContext);
  const { char, hasFakeCaret, isActive } = slots[index] || {};

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex h-10 w-10 items-center justify-center rounded-[8px] bg-brand-chalk text-body-small-regular transition-all min-[390px]:h-12 min-[390px]:w-12',
        isActive && 'z-10 ring-2 ring-black ring-offset-2',
        hasError && 'border-2 border-danger ring-danger'
      )}
      {...props}
      data-testid="input-otp-slot"
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex animate-caret-blink items-center justify-center">
          <div className="h-8 w-px bg-brand-concrete" />
        </div>
      )}
    </div>
  );
});
Slot.displayName = 'InputOTP.Slot';

export const Separator = forwardRef<
  ElementRef<'div'>,
  ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props} data-testid="input-otp-separator">
    <MinusIcon aria-hidden="true" className="size-6 text-secondary" />
  </div>
));
Separator.displayName = 'InputOTP.Separator';
